import React, { useState } from "react";
import {
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { validateRegistrationFields } from "../../utils/validation/validation";
import { registerUser } from "../../services/api/auth";
import { showToast } from "../../store/slices/toastSlice";
import BasicDetailsStep from "./BasicDetailsStep";
import SubscriptionPlanStep from "./SubscriptionPlanStep";
import { ERROR_MESSAGES } from "../../constant/messages";
import { getRegisterInputs } from "../../utils/register.inputs";
import ReviewStep from "./ReviewStep";
import { useNavigate } from "react-router-dom";

const RegisterStepper = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const steps = ["Subscription Plan", "Basic Details", "Review"];

  const initialFormState = {
    first_name: "",
    last_name: "",
    email: "",
    password:"",
    contact: "",
    subscription_plan_id: "",
    plan_details: "",
  };

  const [registerData, setRegisterData] = useState(initialFormState);

  const handleNext = () => {
    if (activeStep === 0 && !registerData.subscription_plan_id) {
      dispatch(
        showToast({
          message: ERROR_MESSAGES.AUTH.EMPTY_SUBSCRIPTION_PLAN,
          severity: "error",
        })
      );
      return;
    }

    if (activeStep === 1) {
      const validationResult = validateRegistrationFields(registerData);
      if (!validationResult.valid) {
        dispatch(
          showToast({ message: validationResult.message, severity: "error" })
        );
        return;
      }
    }

    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleReset = () => {
    setRegisterData(initialFormState);
    setActiveStep(0);
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    const validationResult = validateRegistrationFields(registerData);
    if (!validationResult.valid) {
      dispatch(
        showToast({ message: validationResult.message, severity: "error" })
      );
      return;
    }

    try {
      const data = await registerUser(registerData);
      if (data.responseCode === 201) {
        localStorage.setItem("registerSuccess", data.message);
        dispatch(showToast({ message: data.message, severity: "success" }));
        navigate("/login");
      } else {
        dispatch(showToast({ message: data.message, severity: "error" }));
      }
    } catch (error) {
      dispatch(showToast({ message: error.message, severity: "error" }));
    }
  };

  return (
    <>
      <Stepper activeStep={activeStep} alternativeLabel>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ mt: 4 }}>
        {/* Render steps conditionally */}
        {activeStep === 0 && (
          <SubscriptionPlanStep
            registerData={registerData}
            setRegisterData={setRegisterData}
            dispatch={dispatch}
          />
        )}

        {activeStep === 1 && (
          <BasicDetailsStep
            inputs={getRegisterInputs()}
            registerData={registerData}
            setRegisterData={setRegisterData}
          />
        )}

        {activeStep === 2 && <ReviewStep registerData={registerData} />}
      </Box>

      {/* Stepper Navigation */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
        {activeStep > 0 && (
          <Button onClick={handleBack} variant="outlined">
            Back
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <>
            <Button
              variant="contained"
              onClick={handleNext}
              sx={{ backgroundColor: "black" }}
            >
              Next
            </Button>
            {activeStep === 1 && (
              <Button
                type="button"
                variant="text"
                onClick={handleReset}
                sx={{ color: "gray" }}
              >
                Reset
              </Button>
            )}
          </>
        ) : (
          <Button
            type="submit"
            variant="contained"
            onClick={handleRegister}
            sx={{ backgroundColor: "black" }}
          >
            Complete Registration
          </Button>
        )}
      </Box>
    </>
  );
};

export default RegisterStepper;
